/**
 * Scene duration allocator — pure TypeScript mirror of
 * app/services/ai/scene_allocator.py.
 *
 * Distributes a total ad duration across N scenes where each scene
 * duration must be in {5, 10, 15} (the only values Seedance accepts).
 *
 * Tie-breaker: prefer the most uniform distribution. When the total is
 * not evenly divisible by scene_count (making uniform impossible), fill
 * from the front with larger values.
 */

export type SceneDuration = 5 | 10 | 15

export interface AllocateOptions {
  /** Total seconds (must be a positive multiple of 5). */
  total: number
  /**
   * Optional. When omitted, picks the smallest scene count that fits
   * with all-5s preference (i.e. total / 5 scenes each of 5s).
   */
  sceneCount?: number
}

export interface AllocateResult {
  durations: SceneDuration[]
  /** True when the input was valid and distributable without coercion. */
  exact: boolean
}

const VALID: readonly SceneDuration[] = [15, 10, 5]

function allocateFixed(total: number, count: number): SceneDuration[] {
  // Feasibility bounds
  if (total < 5 * count) {
    throw new Error(
      `Cannot fit ${count} scene(s) into ${total}s — minimum is ${5 * count}s (${count} × 5s)`,
    )
  }
  if (total > 15 * count) {
    throw new Error(
      `Cannot fit ${count} scene(s) into ${total}s — maximum is ${15 * count}s (${count} × 15s)`,
    )
  }
  if (total % 5 !== 0) {
    throw new Error(`total must be divisible by 5, got ${total}`)
  }

  // --- Uniform check first (tie-breaker: prefer uniform) ---
  if (total % count === 0) {
    const per = total / count
    if (per === 5 || per === 10 || per === 15) {
      return Array(count).fill(per) as SceneDuration[]
    }
  }

  // --- Fill from front with largest valid values ---
  const scenes: SceneDuration[] = []
  let remaining = total
  for (let i = 0; i < count; i++) {
    const slotsLeft = count - i
    let assigned = false
    for (const candidate of VALID) {
      const after = remaining - candidate
      const remainingSlots = slotsLeft - 1
      if (remainingSlots === 0) {
        if (after === 0) {
          scenes.push(candidate)
          remaining = 0
          assigned = true
          break
        }
      } else {
        const lo = 5 * remainingSlots
        const hi = 15 * remainingSlots
        if (lo <= after && after <= hi && after % 5 === 0) {
          scenes.push(candidate)
          remaining = after
          assigned = true
          break
        }
      }
    }
    if (!assigned) {
      throw new Error(
        `No valid distribution for total=${total}, count=${count} (stuck at position ${i}, remaining=${remaining})`,
      )
    }
  }
  return scenes
}

/**
 * Distribute `total` seconds across scenes where each is in {5, 10, 15}.
 *
 * Throws when total is not divisible by 5 or sceneCount is incompatible.
 *
 * Examples:
 *   allocate({ total: 30 })             -> [5,5,5,5,5,5]   (smallest count)
 *   allocate({ total: 30, sceneCount: 3 }) -> [10,10,10]
 *   allocate({ total: 30, sceneCount: 2 }) -> [15,15]
 *   allocate({ total: 25, sceneCount: 3 }) -> [15,5,5]
 *   allocate({ total: 45, sceneCount: 3 }) -> [15,15,15]
 */
export function allocate(options: AllocateOptions): AllocateResult {
  const { total, sceneCount } = options

  if (!Number.isInteger(total) || total <= 0) {
    throw new Error(`total must be a positive integer, got ${total}`)
  }
  if (total % 5 !== 0) {
    throw new Error(`total must be divisible by 5, got ${total}`)
  }

  if (sceneCount !== undefined) {
    if (!Number.isInteger(sceneCount) || sceneCount <= 0) {
      throw new Error(`sceneCount must be a positive integer, got ${sceneCount}`)
    }
    const durations = allocateFixed(total, sceneCount)
    // exact = uniform (every value is the same)
    const exact = durations.every((d) => d === durations[0])
    return { durations, exact }
  }

  // No sceneCount: pick smallest count that fits.
  // Minimum scenes = ceil(total / 15) so we don't exceed 15s per scene.
  const minScenes = Math.max(1, Math.ceil(total / 15))
  const maxScenes = total / 5
  if (maxScenes < minScenes) {
    throw new Error(`Cannot distribute ${total}s into any number of scenes using {5,10,15}`)
  }
  const durations = allocateFixed(total, minScenes)
  const exact = durations.every((d) => d === durations[0])
  return { durations, exact }
}
